import { useEffect } from "react";
import { ChevronLeft, ChevronRight, Pencil } from "lucide-react";
import Modal from "shared/Modal";
import Button from "shared/Button";
import StatusPill from "shared/StatusPill";
import LoggedOn from "shared/LoggedOn";
import type { Meeting } from "../api/MeetingsApi";
import { CONDUCTOR_TONE, formatMeetingDate } from "./meetingUtils";

interface MeetingDetailModalProps {
  meeting: Meeting;
  canEdit: boolean;
  onEdit: (m: Meeting) => void;
  onClose: () => void;
  /** The next-newer / next-older meeting in the current list; omitted at either end. */
  onNewer?: () => void;
  onOlder?: () => void;
}

function NoteBlock({ label, text }: { label: string; text: string | null }) {
  return (
    <section>
      <h3 className="text-xs font-medium text-muted-foreground mb-1.5">{label}</h3>
      {text ? (
        <p className="text-sm text-foreground whitespace-pre-wrap bg-surface-muted rounded-lg p-3">{text}</p>
      ) : (
        <p className="text-sm text-muted-foreground italic">Nothing recorded.</p>
      )}
    </section>
  );
}

/** Read-only view of one meeting: both note blocks and the notes. */
export default function MeetingDetailModal({ meeting, canEdit, onEdit, onClose, onNewer, onOlder }: MeetingDetailModalProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      if (e.key === "ArrowLeft" && onNewer) onNewer();
      if (e.key === "ArrowRight" && onOlder) onOlder();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onNewer, onOlder]);

  return (
    <Modal
      size="lg"
      title={meeting.employee_name}
      description={
        <span className="inline-flex items-center gap-2 flex-wrap">
          {formatMeetingDate(meeting.meeting_date)}
          <StatusPill tone={CONDUCTOR_TONE[meeting.conductor_role]} size="sm">{meeting.conductor_role}</StatusPill>
          {meeting.conductor_employee_name && <span>with {meeting.conductor_employee_name}</span>}
        </span>
      }
      onClick={onClose}
      closeButtonCLick={onClose}
      footer={
        <>
          <div className="mr-auto flex gap-2">
            <Button variant="outline" onClick={onNewer} disabled={!onNewer} aria-label="Newer meeting" title="Newer meeting (←)">
              <ChevronLeft className="w-4 h-4 inline" /> Newer
            </Button>
            <Button variant="outline" onClick={onOlder} disabled={!onOlder} aria-label="Older meeting" title="Older meeting (→)">
              Older <ChevronRight className="w-4 h-4 inline" />
            </Button>
          </div>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {canEdit && (
            <Button variant="primary" onClick={() => onEdit(meeting)}>
              <Pencil className="w-4 h-4 mr-1.5 inline" />
              Edit
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-5">
        <NoteBlock label="Issue or topic raised" text={meeting.before_meeting_notes} />
        <NoteBlock label="Outcome" text={meeting.after_meeting_remarks} />
        <NoteBlock label="Follow-up" text={meeting.follow_up} />

        <LoggedOn recordDate={meeting.meeting_date} recordedAt={meeting.created_at} />

        <p className="text-xs text-muted-foreground">
          Logged by {meeting.created_by || "—"}
          {meeting.updated_by ? ` · last updated by ${meeting.updated_by}` : ""}
        </p>
      </div>
    </Modal>
  );
}
